import { NavLink, Outlet } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/Button";
import { useMe } from "@/api/auth";
import { cn } from "@/lib/utils";

// Single top bar — no sidebar. The two creation flows are reached from the
// landing chooser (the Brand links home); History (everyone's completed work)
// and the admin Dashboard live up here as small text links on the right.
const ADMIN_LINKS = [
  { to: "/history", label: "History" },
  { to: "/dashboard", label: "Dashboard" },
  { to: "/saved-properties", label: "Properties" },
] as const;

export function AppShell() {
  const me = useMe();

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20 border-b border-rule/70 bg-canvas/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between gap-6 px-6">
          <NavLink to="/" className="block">
            <Brand size="md" />
          </NavLink>

          <div className="flex items-center gap-5">
            <nav className="hidden items-center gap-1 sm:flex">
              {ADMIN_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    cn(
                      "rounded-md px-3 py-1.5 text-[0.88rem] font-medium transition-colors",
                      isActive
                        ? "bg-primary-soft text-primary-ink"
                        : "text-ink-soft hover:bg-surface-2 hover:text-ink",
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden h-6 w-px bg-rule sm:block" aria-hidden />

            <span className="hidden text-[0.84rem] text-ink-muted sm:inline">
              {me.data?.email ?? "—"}
            </span>
            <form action="/logout" method="get">
              <Button intent="ghost" size="sm" type="submit">
                Sign out
              </Button>
            </form>
          </div>
        </div>
      </header>

      {/* 1200px was chosen when every screen here was a form or a list, and
          1600px still is right for those — individual prose blocks keep their
          own narrower measures either way. The drawing workspace does not go
          through here at all: it is a full-screen overlay over this shell, so
          it takes the whole window without the shell needing a mode. */}
      <main className="mx-auto max-w-[1600px] px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
}
